import { useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { TableWrapper } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/topics-table/TopicsTable.styled";
import type { IModifiableTopic } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import { getWidgetNoDataProps } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import { allTrackers } from "app/services/track/track";

import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";

import { TopicCard } from "./TopicCard";

interface ITopicsTableProps {
    topics: IModifiableTopic[];
    selectedTopic: string;
    totalPromptsCount: number;
    onTopicSelection: (topic: string) => void;
    onTopicNameChange: (oldName: string, newName: string) => void;
    onTopicCreation: (topic: string) => void;
    onTopicDeletion: (topic: string) => void;
    isLoading: boolean;
    error?: boolean;
}

export const TopicsTable = ({
    topics,
    selectedTopic,
    totalPromptsCount,
    onTopicSelection,
    onTopicNameChange,
    onTopicCreation,
    onTopicDeletion,
    isLoading,
    error,
}: ITopicsTableProps) => {
    const [hasNewTopicDraft, setNewTopicDraft] = useState<boolean>(false);
    const topicNames = useMemo<string[]>(
        () => topics?.reduce((acc, { name }) => [...acc, name], [] as string[]) ?? [],
        [topics],
    );

    const onNewTopicClick = (): void => {
        if (hasNewTopicDraft) {
            return;
        }

        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "create topic/open");
        setNewTopicDraft(true);
    };

    const onNewTopicCancel = (): void => {
        if (!hasNewTopicDraft) {
            return;
        }

        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "create topic/cancel");
        setNewTopicDraft(false);
    };

    const onCreateTopic = (name: string): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "create topic/submit");
        setNewTopicDraft(false);
        onTopicCreation?.(name);
    };

    const canTopicBeDeleted = (topic: IModifiableTopic): boolean => {
        //prevent the deletion of the only topic left aside from "All Topics"
        if (topic.topicId === ALL_TOPICS_NAME || topics.length <= 2) {
            return false;
        }

        //prevent the deletion of the only populated topic
        return topic.prompts.length !== totalPromptsCount;
    };

    return (
        <TableWrapper
            borderRight={`1px solid ${colorsPalettes.carbon[100]}`}
            minWidth="350px"
            width="350px"
        >
            <WidgetContainer
                key={topicNames.join(",")}
                {...getWidgetNoDataProps(!topics, error)}
                title={translate("llm.prompts.edit.topics-table.title")}
                isLoading={isLoading}
                loaderWidth="100%"
                loaderHeight={400}
                headerHeight={60}
                withBorder
            >
                <Flex flexDirection="column">
                    {topics.map((topic) => {
                        const { topicId, name, prompts } = topic;
                        return (
                            <TopicCard
                                key={topicId}
                                topicName={
                                    topicId === ALL_TOPICS_NAME ? translate("llm.prompts.edit.all-topics") : name
                                }
                                allTopicNames={topicNames}
                                prompts={prompts}
                                onEditApprove={onTopicNameChange}
                                isSelected={name === selectedTopic}
                                // Select by the topic's real name, not the (possibly translated) display label
                                // TopicCard shows, since the two can differ for the "All Topics" pseudo-topic.
                                onSelection={() => onTopicSelection(name)}
                                onDelete={canTopicBeDeleted(topic) ? onTopicDeletion : undefined}
                                canBeEdited={topicId !== ALL_TOPICS_NAME}
                            />
                        );
                    })}
                    {hasNewTopicDraft ? (
                        <TopicCard
                            key="new-topic-draft"
                            topicName=""
                            allTopicNames={topicNames}
                            isSelected
                            canBeEdited
                            onEditApprove={onCreateTopic}
                            onCancelEdit={onNewTopicCancel}
                        />
                    ) : null}
                    <Box margin="4px">
                        <IconButton
                            data-automation="topics-table-add"
                            type="flat"
                            iconName="add"
                            isLoading={isLoading}
                            isDisabled={hasNewTopicDraft}
                            onClick={onNewTopicClick}
                        >
                            {translate("llm.prompts.edit.topics-table.add")}
                        </IconButton>
                    </Box>
                </Flex>
            </WidgetContainer>
        </TableWrapper>
    );
};
